<template>
  <div class="robot-selfie" ref="robotRoot">
    <section class="selfie-section live-area" :data-orientation="orientation" :style="dimensions" :data-svg="Boolean(svg)">
      <div style class="video-container" :style="dimensions" ref="videoDiv">
        <video muted="true" playsinline id="videoInput" ></video>

        <Tip v-for="tip in currentTips" v-bind:key="tip" :text="tip" />

        <div v-if="face && !panel.visible" class="perfection" :style="position"></div>

        <VideoError
          v-if="error"
          :error="error"
          :videoErrorHeading="videoErrorHeading"
          :videoErrorText1="videoErrorText1"
          :videoErrorText2="videoErrorText2"
          :videoStartButton="videoStartButton"
        />
        <StartingVideo v-if="startingVideo" :loadingText="videoLoadingText" />

        <button class="draw-button video-control bottom-left" v-if="!playing && !startingVideo" @click="enableVideo">{{ videoStartButton }}</button>

        <ProgressButton
          class="video-control bottom-right"
          v-if="playing"
          @click="sendImage"
          :disabled="drawing || Boolean(submit)"
          :in-progress="drawing || Boolean(submit)"
          aria-label="Draw"
          title="Draw"
        >
          <RadioCheckedSVG />
        </ProgressButton>

        <ToggleButton
          class="video-control bottom-left control-panel-toggle"
          v-if="playing && panel.allowCustomSettings"
          @click="toggleControls"
          :active="panel.visible"
          aria-label="Toggle controls"
        >
          <EqualizerSVG />
        </ToggleButton>

        <control-panel v-if="panel.allowCustomSettings" :data-visible="panel.visible" class="control-panel" v-model="panel"></control-panel>
      </div>
      <div class="drawing-container" :style="dimensions" ref="drawingDiv">

        <div class="svg" v-html="svg" :style="dimensions" />

        <ActionButton
          class="video-control bottom-left"
          v-if="svg"
          @click="rejectImage"
          type="back"
          aria-label="Reject image and try again"
        >
            <CrossSVG />
        </ActionButton>

        <ActionButton
          class="video-control bottom-right"
          v-if="svg"
          @click="saveImage"
          type="next"
          aria-label="Accept image"
        >
          <CheckmarkSVG />
        </ActionButton>

      </div>
    </section>
  </div>
</template>

<script>
/* global cv */
import 'md-gum-polyfill'
import axios from 'axios'
import ControlPanel from './ControlPanel'
import StartingVideo from "@/components/StartingVideo.vue"
import VideoError from "@/components/VideoError.vue"
import ToggleButton from "@/components/ToggleButton.vue"
import ProgressButton from "@/components/ProgressButton.vue"
import ActionButton from "@/components/ActionButton.vue"
import Tip from "@/components/Tip.vue"

import EqualizerSVG from "@/assets/equalizer.svg"
import RadioCheckedSVG from "@/assets/radio-checked2.svg"
import CrossSVG from "@/assets/cross.svg"
import CheckmarkSVG from "@/assets/checkmark.svg"

const FPS = 10
const IDEAL_WIDTH = 1024
const IDEAL_HEIGHT = 768

function b64ToUint8Array (b64Image) {
  const img = atob(b64Image.split(',')[1])
  const img_buffer = []
  let i = 0
  while (i < img.length) {
      img_buffer.push(img.charCodeAt(i))
      i++
  }
  return new Uint8Array(img_buffer)
}

async function loadCascade (name) {
    try {
      // name used as url
      const response = await axios.get(name, { responseType: 'arraybuffer' })
      const data = new Uint8Array(response.data)
      // name used as path to store in memfs
      cv.FS_createDataFile('/', name, data, true, false, false)
      // now load from memfs
      const classifier = new cv.CascadeClassifier()
      classifier.load(name)
      return classifier
    } catch (err) {
      console.log('Failed to load ' + name + ' status: ' + err)
      throw err
    }
}

export default {
  name: "robot-selfie",
  props: ["site", "videoErrorHeading", "videoErrorText1", "videoErrorText2", "videoStartButton", "videoLoadingText" ],
  data: () => ({
    startingVideo: false,
    playing: false,
    drawing: false,
    submit: false,
    svg: null,
    face: null,
    videoWidth: 640,
    videoHeight: 480,
    orientation: "landscape",
    panel: {
      visible: false,
      allowCustomSettings: false,
      outline: 1.2,
      squiggleTurn: 0.5,
      squiggleSkew: 0.25,
      squiggleLength: 0.25,
      squiggleShading: 0.8,
      eyes: false,
      nose: true,
      mouth: true
    },
    error: false,

    // Keep track of how the user is liking their portraits
    // Setting to -1 forces a recalculation of the currentTips computed prop after the
    // video becomes available. Without this, the first tip is rendered (and animated)
    // before everything else is in a state to see it.
    imagesCreated: -1,
    imagesDeclined: -1,
    tips: [],

    // cvReady and faceTimeout are not specified here as they don't need to
    // be observable by the UI and can just be object properties
  }),
  computed: {
    position () {
      // see facedetect.py convert_to_svg() for corresponding server side calcs
      return {
        left: `${this.videoWidth - this.face.width * (1 + 0.3) - this.face.x}px`, // mirrored
        top: `${this.face.y - this.face.height * 0.4}px`,
        width: `${this.face.width * (1 + 0.3 * 2)}px`,
        height: `${this.face.height * (1 + 0.4 * 2)}px`,
      }
    },
    dimensions () {
      return {
        width: `${this.videoWidth - 6}px`,
        height: `${this.videoHeight}px`,
        maxWidth: "950px",
        // maxHeight: "calc(100vh - 10px)",
      }
    },
    panelSummary () {
      return`${this.panel.outline}-${this.panel.squiggleTurn}-${this.panel.squiggleSkew}-${this.panel.squiggleLength}-${this.panel.squiggleShading}-${this.panel.eyes?'t':'f'}${this.panel.nose?'t':'f'}${this.panel.mouth?'t':'f'}`
    },
    currentTips () {
      if (this.tips[this.imagesDeclined]) {
        return [this.tips[this.imagesDeclined]]
      }
      return []
    }
  },
  created () {
    // async request opencv.js to avoid blocking on page load
    this.cvLoad = false
    const cvScript = document.createElement('script')
    cvScript.setAttribute('src', '/opencv.js')
    document.head.appendChild(cvScript)
    cvScript.addEventListener('load', () => {
      this.cvLoad = true
      if (this.onCvLoad) {
        this.onCvLoad()
      }
    })
  },
  mounted () {
    console.log(`cv.calledRun on mounted: ${this.cvLoad && cv.calledRun}`)

    const loadPanelDefaults = async () => {
      try {
        const response = await axios.get("/cms/api/v2/site/" + this.site + "/")

        const data = response.data
        this.panel.allowCustomSettings = data.allow_custom_settings
        this.panel.outline = data.outline
        this.panel.squiggleTurn = data.squiggle_turn
        this.panel.squiggleSkew = data.squiggle_skew
        this.panel.squiggleLength = data.squiggle_length
        this.panel.squiggleShading = data.squiggle_shading
        this.panel.eyes = data.eyes
        this.panel.nose = data.nose
        this.panel.mouth = data.mouth
        this.panel.resolution = data.resolution
        this.panel.strokeWidth = data.stroke_width
        this.panel.strokeOpacity = data.stroke_opacity
        this.panel.strokeColor = data.stroke_color
        this.panel.fillWidth = data.fill_width
        this.panel.fillSpacing = data.fill_spacing
        this.panel.fillOpacity = data.fill_opacity
        this.panel.fillOverpaint = data.fill_overpaint
        this.panel.fillThresholds = data.fill_thresholds
        this.panel.fillColors = data.fill_colors

        data.portrait_tips.forEach((tip) => {
          this.tips[tip.rejected_number] = tip.text
        })

      } catch (err) {
        console.log(err)
        if (err.response.status === 404) {
          console.log("No site settings found for " + this.site)
        } else {
          console.log(err)
        }
      }
    }

    const setupClassifiers = async () =>  {
      try {
        // load pre-trained classifiers
        this.classifierFace = await loadCascade('/haarcascade_frontalface_alt.xml')
        this.classifierEye = await loadCascade('/haarcascade_eye.xml')
        this.classifierNose = await loadCascade('/haarcascade_mcs_nose.xml')
        this.classifierMouth = await loadCascade('/haarcascade_mcs_mouth.xml')
      } catch (err) {
        console.log("An error occurred setting up the classifiers! " + err)
        this.error = 'loading from the server'
      }
    }

    const initialise = async () => {
      // cv library initialised, load classifiers
      await setupClassifiers()
      this.setup()
    }

    const loaded = () => {
      // cv library loaded, check if its initialised
      if (cv.calledRun) {
        initialise()
      } else {
        cv.onRuntimeInitialized = initialise
      }
    }

    // debugger // eslint-disable-line
    loadPanelDefaults()

    if (this.cvLoad) {
      loaded()
    } else {
      this.onCvLoad = loaded
    }
  },
  beforeDestroy () {
      this.classifierFace.delete()
      this.classifierEye.delete()
      this.classifierNose.delete()
      this.classifierMouth.delete()
  },
  methods: {
    setup () {
      try {
        this.gray = new cv.Mat()
        this.faces = new cv.RectVector()
        this.eyes = new cv.RectVector()
        this.noses = new cv.RectVector()
        this.mouths = new cv.RectVector()
      } catch (err) {
        console.log("An error occurred setting up the data structures " + err)
        this.error = 'initialising vision'
      }
      this.cvReady = true
      if (this.onCvReady) {
        this.onCvReady()
        delete this.onCvReady
      }
    },
    teardown () {
      // clean up from processVideo
      this.face = null
      this.src.delete()
      this.src = null
      this.dst.delete()
      this.dst = null
      // clean up from setup
      this.gray.delete()
      this.faces.delete()
      this.eyes.delete()
      this.noses.delete()
      this.mouths.delete()
      // clean up playing
      clearTimeout(this.faceTimeout)
      this.faceTimeout = null
      this.cvReady = false
      this.imagesCreated = -1
      this.imagesDeclined = -1
      if (this.removeEventListeners) {
        this.removeEventListeners()
      }
    },
    toggleControls() {
      this.panel.visible = !this.panel.visible
    },
    setFaceTimeout() {
      // Make sure there's not multiple timers being set
      if (this.faceTimeout) {
        clearTimeout(this.faceTimeout)
      }
      const delay = 1000/FPS
      this.faceTimeout = setTimeout(this.processVideo, delay)
    },
    stopVideo() {
      const video = document.getElementById('videoInput')
      if (video.srcObject) {
        video.srcObject.getTracks().forEach(function(track) {
          track.stop()
        })
        this.playing = false
        video.width = null
        video.height = null
      }
    },
    async enableVideo () {
      if (this.playing) {
        this.startingVideo = false
        return
      }

      this.startingVideo = true
      this.error = false

      if (this.cvReady === false) {
        // if false, rather than undefined, then the video's been stopped
        // and torn down so need to setup again
        this.setup()
      }

      const video = document.getElementById('videoInput')

      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          {
            video: {
              width: {ideal: IDEAL_WIDTH},
              height: {ideal: IDEAL_HEIGHT},
              facingMode: 'user'
            },
            audio: false
          }
        )
        video.srcObject = stream
        video.play()

        this.imagesCreated = 0
        this.imagesDeclined = 0

        const handleResize = () => {
          let w = video.videoWidth
          let h = video.videoHeight
          const aspectRatio = h / w
          // debugger // eslint-disable-line

          if (w && h) {
            // Likely video is wider or higher than browser when viewed on mobile
            // device, so need to adjust width and height accordingly.
            //
            // Why can't we just do this with CSS? Because many things depends on exact
            // pixel dimensions for the video and aspect ratio matters.
            const windowHeight = window.innerHeight

            const maxWidth = this.$refs.robotRoot.clientWidth
            const maxHeight = windowHeight - 6 // take borders into account

            // Adjust size to fit vertically
            if (h > maxHeight) {
              h = maxHeight
              w = h / aspectRatio
            }

            // And then adjust again for width
            if (w > maxWidth) {
              w = maxWidth
              h = w * aspectRatio
            }

            if (w >= h) {
              this.orientation = "landscape"
            } else {
              this.orientation = "portrait"
            }

            video.width = w
            video.height = h
            this.submit = false

            const setupCvVideo = () => {
              if (this.src) this.src.delete()
              this.src = new cv.Mat(h, w, cv.CV_8UC4)
              if (this.dst) this.dst.delete()
              this.dst = new cv.Mat(h, w, cv.CV_8UC4)
              this.video = video
              this.videoWidth = w
              this.videoHeight = h
              const thirds = Math.min(w, h)/3
              this.face = new cv.Rect((w-thirds)/2, (h-thirds)/2, thirds, thirds)
              this.setFaceTimeout()
              this.$refs.robotRoot.scrollIntoView({block: 'start', inline: 'nearest', behavior: 'smooth'})
            }

            console.log(`cvReady on resize: ${this.cvReady}`)

            if (this.cvReady) {
              setupCvVideo()
            } else {
              this.onCvReady = setupCvVideo
            }
          }
        }

        video.addEventListener('resize', handleResize, false)
        window.addEventListener('resize', handleResize, false)
        this.removeEventListeners = () => {
          video.removeEventListener('resize', handleResize)
          window.removeEventListener('resize', handleResize)
        }

        this.playing = true
      } catch (err) {
        this.error = 'setting up your video camera'
        console.log("An error occurred setting up the video! " + err)
      }

      this.startingVideo = false
    },
    copyFrame (frame) {
      // this is broadly equivalent to cv.VideoCapture.read()
      const canvas = document.createElement('canvas')
      cv.imshow(canvas, this.src)
      canvas.width = this.video.width
      canvas.height = this.video.height
      const context = canvas.getContext('2d')
      context.drawImage(this.video, 0, 0, this.video.videoWidth, this.video.videoHeight, 0, 0, canvas.width, canvas.height)
      frame.data.set(context.getImageData(0, 0, canvas.width, canvas.height).data)
    },
    processVideo () {
      try {
        if (this.cvReady && !this.svg && !this.panel.visible) {
          if (!this.playing) {
            this.teardown()
            return
          }
          // start processing
          this.copyFrame(this.src)
          this.src.copyTo(this.dst)
          cv.cvtColor(this.dst, this.gray, cv.COLOR_RGBA2GRAY, 0)
          // detect faces.
          this.classifierFace.detectMultiScale(this.gray, this.faces, 1.3, 4, 0, new cv.Size(30,30))

          // draw faces, look for the largest detected face
          let biggest = null
          for (let i = 0; i < this.faces.size(); ++i) {
            let face = this.faces.get(i)
            if (i == 0 || biggest.width < face.width) {
              biggest = face
            }
          }

          // look for features
          if (biggest) {
            this.face = biggest
            // debugger   // eslint-disable-line
            //const point1 = new cv.Point(face.x, face.y)
            //const point2 = new cv.Point(face.x + face.width, face.y + face.height)
            //cv.rectangle(this.dst, point1, point2, [255, 0, 0, 255])

            let roi = this.gray.roi(this.face)
            this.classifierEye.detectMultiScale(roi, this.eyes, 1.2, 3, 0, new cv.Size(15,15))
            /*
            for (let j = 0; j < this.eyes.size(); ++j) {
              const eye = this.eyes.get(j)
              const p1 = new cv.Point(face.x + eye.x, face.y + eye.y)
              const p2 = new cv.Point(face.x + eye.x + eye.width, face.y + eye.y + eye.height)
              cv.rectangle(this.dst, p1, p2, [0, 255, 0, 255])
            }
            */
            this.classifierNose.detectMultiScale(roi, this.noses, 1.2, 4, 0, new cv.Size(15,5))
            /*
            for (let j = 0; j < this.noses.size(); ++j) {
              const nose = this.noses.get(i)
              const p1 = new cv.Point(face.x + nose.x, face.y + nose.y)
              const p2 = new cv.Point(face.x + nose.x + nose.width, face.y + nose.y + nose.height)
              cv.rectangle(this.dst, p1, p2, [0, 255, 0, 255])
            }
            */
            const halfheight = Math.floor(this.face.height * 0.6)
            const halfface = Object.assign({}, this.face, {y: this.face.y + halfheight, height: this.face.height - halfheight})
            roi = this.gray.roi(halfface)
            this.classifierMouth.detectMultiScale(roi, this.mouths, 1.2, 4, 0, new cv.Size(15,5))
            /*
            for (let j = 0; j < this.mouths.size(); ++j) {
              const mouth = this.mouths.get(i)
              const p1 = new cv.Point(face.x + mouth.x, face.y + halfheight + mouth.y)
              const p2 = new cv.Point(face.x + mouth.x + mouth.width, face.y + halfheight + mouth.y + mouth.height)
              cv.rectangle(this.dst, p1, p2, [0, 0, 255, 255])
            }
            */
          } else {
            const empty = new cv.Rect()
            this.eyes.resize(0, empty)
            this.noses.resize(0, empty)
            this.mouths.resize(0, empty)
          }
          if (this.submit &&
              // wait until the person's looking at the camera
              ((this.faces.size() > 0 && this.eyes.size() == 2
                && ((this.panel.mouth && this.mouths.size() == 1)
                  || (this.panel.nose && this.noses.size() == 1))) ||
              // or submit anyway if this doesn't happen quickly
              this.submit + 4000 < Date.now())) {
            this.processImage()
            this.submit = false
          }
        }
        this.setFaceTimeout()
      } catch (err) {
        this.error = 'processing your video'
        console.log(err)
      }
    },
    sendImage () {
      this.submit = Date.now()
    },
    async processImage () {
      this.drawing = true
      this.submit = false

      const canvas = document.createElement('canvas')
      cv.imshow(canvas, this.src)

      const b64Image = canvas.toDataURL('image/jpeg')
      const u8Image  = b64ToUint8Array(b64Image)

      const data = new FormData()
      data.append('image', new Blob([ u8Image ], {type: "image/jpg"}))
      const face = this.face
      const eyes = []
      for (let i = 0; i < this.eyes.size(); ++i) {
        eyes.push(this.eyes.get(i))
      }
      const nose = this.noses.size() == 1 ? this.noses.get(0) : null
      let mouth = this.mouths.size() == 1 ? this.mouths.get(0) : null
      if (mouth) {
        const halfheight = Math.floor(face.height * 0.6)
        mouth = Object.assign({}, mouth, {y: mouth.y + halfheight})
      }
      data.append('features', JSON.stringify({face, eyes, nose, mouth}))
      data.append('config', JSON.stringify(this.panel))

      try {
        const response = await axios.post(`/api/upload/`, data, {
          headers: {
            'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
          }
        })
        // debugger // eslint-disable-line
        this.svg = response.data
      } catch (err) {
        this.error = 'drawing your picture'
        console.log(err)
      }
      this.imagesCreated++
      this.drawing = false
    },
    download () {
      // debugger // eslint-disable-line
      const blob = new Blob([this.svg], {
        type: "image/svg+xml"
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.target = "_blank"
      link.download = `robot-selfie-${this.panelSummary}.svg`
      link.href = url
      link.click()
    },
    saveImage() {
      localStorage['panel'] = this.panelSummary
      localStorage['svg'] = this.svg
      this.$emit('selfieSaved')
    },
    rejectImage() {
      this.svg = null
      this.imagesDeclined++

      if (this.imagesDeclined == 1) {
        this.currentTip = null
        this.currentTip = "Some tip"
      }
      if (this.imagesDeclined == 2) {
        this.currentTip = null
        this.currentTip = "Another tip"
      }

    }
  },
  components: {
    ControlPanel,
    StartingVideo,
    VideoError,
    ToggleButton,
    ProgressButton,
    ActionButton,
    EqualizerSVG,
    RadioCheckedSVG,
    CrossSVG,
    CheckmarkSVG,
    Tip,
  }
}
</script>

<style lang="scss" scoped>


    .robot-selfie {
      scroll-margin-top: 70px;

    .live-area {
      display: flex;
      flex-flow: row nowrap;
      align-items: flex-start;
      justify-content: flex-start;
      margin: auto;
      max-width: 640px;
      border: solid 3px $color-grey;
      border-radius: 15px;
      overflow: hidden;

      &:last-child {
        margin-bottom: 0;
      }

      // &[data-orientation="portrait"] {
      //   #videoInput {
      //     max-width: 100%;
      //     height: auto;
      //   }
      // }
      // &[data-orientation="landscape"] {
      //   #videoInput {
      //     width: auto;
      //     max-height: 100%;
      //   }
      // }

      .video-container {
        position: relative;
        display: flex;
        flex-flow: row wrap;
        justify-content: center;
        align-items: center;
        // height: 480px;
        // width: 480px;
        overflow: hidden;
        flex-shrink: 0;

        #videoInput {
          position: absolute;
          top: 0;
          left: 0;
          display: block;
          margin: auto;
          transform: scaleX(-1);
        }
      }


      .perfection {
        position: absolute;
        border: solid 5px fade-out($color: $color-red, $amount: 0.5);
        border-radius: 5px;
        z-index: 300;
        pointer-events: none;
      }

      .drawing-container {
        display: flex;
        flex-flow: row nowrap;
        flex-shrink: 0;
        justify-content: center;
        align-items: center;
        position: relative;
        // height: 480px;
        // width: 480px;
        overflow: hidden;
        border-radius: 15px;

        .svg {
          display: flex;
          flex-flow: row nowrap;
          justify-content: center;
          align-items: center;

          &::v-deep svg {
            transform: scaleX(-1);
            max-height: 100%;
            max-width: 100%;
            height: 100%;
          }
        }
      }
    }

    .video-control {
      position: absolute;
      z-index:400;

      &.bottom-left {
        bottom: 20px;
        left: 20px;
      }
      &.bottom-right {
        bottom: 20px;
        right: 20px;
      }
    }

    .button-container {
      position: absolute;
      top: 0;
      left: 0;
    }

    .draw-button {
      display: inline-block;
      height: 60px;
      width: 60px;
      padding: 0.5em;
      margin: auto;
      font-size: 14px;
      text-align: center;
      border-radius: 50%;
      // border: solid 3px $color-grey-dark;
      border: none;
      outline: none;
      background: $color-grey-light;
      cursor: pointer;
      transition: all 0.1s ease-in-out;

      // -webkit-box-shadow: 10px 10px 5px 0px rgba(0,0,0,0.75);
      // -moz-box-shadow: 10px 10px 5px 0px rgba(0,0,0,0.75);
      box-shadow: 2px 2px 10px 2px rgba(0,0,0,0.2);

      &:hover {
        background-color: $color-white;
        box-shadow: 1px 1px 8px 1px rgba(0,0,0,0.2);
      }

      &[data-working="true"] {
        background-color: red;
      }
    }

    .control-panel {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: calc(100% - 80px);
      padding-bottom: 80px;
      max-width: 0;
      transition: all 0.2s ease-in-out;
      z-index: 500;
      overflow: hidden;

      &[data-visible="true"] {
        max-width: 640px;
      }

      @media screen and (max-width: $break-mobile) {
        right: 0;
      }
    }

    .control-panel-toggle {
      z-index: 501;
    }

  }

  [dir="rtl"] .robot-selfie .live-area {
    &[data-svg="true"] {
      .video-container {
        margin-right: -100% !important;
      }
    }
  }

  [dir="ltr"] .robot-selfie .live-area {
    &[data-svg="true"] {
      .video-container {
        margin-left: -100%;
      }
    }
  }
</style>
